import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Direction = {
    Import: "I",
    Export: "E"
};
export type DirectionType = typeof Direction[keyof typeof Direction];
export const Format = {
    CSV: "CSV",
    JSON: "JSON"
};
export type FormatType = typeof Format[keyof typeof Format];
class ImportExport extends Base implements EntityType {
    importExportId: uuid;
    name?: string | null;
    direction?: DirectionType | null;
    format?: FormatType | null;
    metrics?: any | null;
    sources: any[];
    constructor(doc: typeof ImportExport | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            importExportId: genUUID(),
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "importExport";
        // @ts-ignore
        this.importExportId = doc._importExportId || doc.importExportId || doc.importexportid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.direction = doc._direction || doc.direction || null;
        // @ts-ignore
        this.format = doc._format || doc.format || null;
        // @ts-ignore
        this.metrics = doc._metrics || doc.metrics || {};
        // @ts-ignore
        this.sources = doc._sources || doc.sources || [];
    }
    get id(): uuid { return this.importExportId; }
    static DIRECTION_VALUES = ["I", "E"];
    static FORMAT_VALUES = ["CSV", "JSON"];
    addSource(source: any): any {
        if (!source) throw new ValidationError('No nulls allowed in an array!');
        if (!this.sources) this.sources = [];
        this.sources.push(source);
        return source;
    }
    updateSource(source: any) {
        if (!this.sources) this.sources = [];
        const index = this.sources.findIndex(item => item === source);
        if (index !== -1) { this.sources[index] = source; }
        else this.addSource(source);
    }
    removeSource(source: any) {
        if (!this.sources) return;
        let foundItems = this.sources?.filter(item => item === source);
        if (foundItems && foundItems.length > 0) {
            this.sources = this.sources.filter(item => !(item === source));
        }
    }
    abandonSources() { this.sources = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this import/export"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.importExportId; }
    static override getNonRefArrayFields(): string[] { return ["sources"]; }
    static override getDefaultEntries(): ImportExport[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'importExport',
    table: 'IMPORT_EXPORT',
    comment: 'A record of a single data import or export job run for a firm. New imports pull contact and matter data from supported external systems such as Bill4Time; historical records may reference retired sources such as Pipedrive. Exports serialize firm data to CSV or JSON for backup or migration. Stores the job name, direction (I/E), output format, and a metrics blob with run-time statistics.',
    fields: {
        importExportId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this import/export'
        },

        name: {
            type: 'string', comment: 'User name for this import/exmport'
        },

        direction: {
            type: 'string', values: ['I', 'E'], valueNames: ['Import', 'Export'], comment: '[I]mport or [E]xport'
        },

        format: {
            type: 'string', values: ['CSV', 'JSON'], comment: 'For export only, what format shall we export to?'
        },

        metrics: {
            type: 'json', comment: 'Metrics for this import or export'
        },

        sources: {
            type: 'json[]',
            transient: true,
            comment: 'The sources for this import/export'
        }
    },
    hooks: [
        {when: 'before', appliesTo: 'save', method: 'processImportExport', async: false}
    ]
});
    }
}
export default ImportExport;
